import { buildEmail } from './emailTemplateBuilder';

const KEY = 'dhs-email-queue';
const LAST_RUN = 'dhs-email-scheduler-last-run';

const load = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; }
};
const save = (list) => {
  localStorage.setItem(KEY, JSON.stringify(list));
};

/**
 * Scans every policy and drafts a renewal email for any policy that:
 *   - is Active (not cancelled)
 *   - expires within the next 30 days (or is already overdue)
 *   - does NOT already have a draft in the queue for the current renewal cycle
 *
 * The draft is stored locally so the agent can review, dismiss or send it.
 * Once the Resend keys are set, the same queue also feeds the Send flow.
 *
 * Runs at most once per calendar day (guarded by localStorage 'lastRun').
 */
export const runRenewalScheduler = (policies, { force = false } = {}) => {
  const today = new Date();
  const todayKey = today.toISOString().slice(0, 10);
  const lastRun = localStorage.getItem(LAST_RUN);
  if (!force && lastRun === todayKey) return { drafted: 0, skipped: true };

  const queue = load();
  const in30 = today.getTime() + 30 * 24 * 60 * 60 * 1000;
  let drafted = 0;

  (policies || []).forEach((p) => {
    if (!p.policyEnd) return;
    if (p.status === 'Cancelled' || p.paymentStatus === 'Cancelled') return;
    const endTs = new Date(p.policyEnd).getTime();
    if (endTs > in30) return;

    const cycleKey = new Date(p.policyEnd).toISOString().slice(0, 10);
    const existing = queue.find((q) => q.policyId === p.id && q.cycleKey === cycleKey);
    if (existing) return;

    const email = buildEmail('renewal', p);
    queue.push({
      id: 'q_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
      policyId: p.id,
      planNumber: p.planNumber,
      customerName: `${p.firstName} ${p.lastName}`,
      customerEmail: p.email || '',
      renewalDate: p.policyEnd,
      cycleKey,
      subject: email.subject,
      html: email.html,
      text: email.text,
      status: 'draft',
      createdAt: new Date().toISOString(),
      sentAt: null,
      provider: null,
      error: null,
    });
    drafted += 1;
  });

  save(queue);
  localStorage.setItem(LAST_RUN, todayKey);
  return { drafted, skipped: false };
};

export const getQueue = () => load();

export const dismissDraft = (draftId) => {
  const q = load().filter((d) => d.id !== draftId);
  save(q);
};

export const markDraftSent = (draftId, { provider }) => {
  const q = load().map((d) => d.id === draftId ? { ...d, status: 'sent', sentAt: new Date().toISOString(), provider } : d);
  save(q);
};

export const markDraftFailed = (draftId, err) => {
  const q = load().map((d) => d.id === draftId ? { ...d, status: 'failed', error: err } : d);
  save(q);
};

export const clearSent = () => {
  const q = load().filter((d) => d.status !== 'sent');
  save(q);
};

export const isSchedulerActive = () => localStorage.getItem(LAST_RUN);
